import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSuppliersQuery, useGetIsccCertificateQuery } from '@/lib/api/lookupsApi';
import { useConvertUnitsQuery } from '@/lib/api/unitsApi';
import { useModuleLookups, useTranslations, useDebounce } from '@/hooks';
import { normalizeUnit, needsDensity } from '@/lib/utils';
import { buildCountryOptions, EMPTY_LIST } from '@/lib/utils';
import {
  buildLookupOptions,
  buildFeedstockCategoryOptions,
  buildAllocationPreferenceOptions,
  createYesNoOptions,
} from '@/config/formOptions';
import { buildRawMaterialTypeOptions } from '@/config/rawMaterialTypes.constants';
import { buildProductTypeOptions } from '@/config/productTypes.constants';
import { COUNTRY_CODES } from '@/config/countries.constants';
import {
  MULTISITE_TRANSFER_VALUE,
  GHG_DEFAULT_VALUE,
  YES_NO_VALUE,
  CHAIN_OF_CUSTODY_OPTION,
  ESCA_BONUS_CAP,
} from './constants/validateDeclaration.constants';
import { CERTIFICATE_STATES, resolveCertificateState } from './utils/certificateValidity';
import { resolveDeclarationQuantityColumns } from './utils/declarationsUtils';

// Long enough that typing a certificate number is one lookup, not one per keystroke.
const LOOKUP_DEBOUNCE_MS = 500;

/**
 * Looks the supplier certificate up in the ISCC directory and says where the field stands.
 * Read-only: nothing is persisted, and a miss is an answer, not an error.
 */
export const useCertificateVerification = (certificateNumber, dateOfIssuance) => {
  const number = (certificateNumber ?? '').trim();
  const debounced = useDebounce(number, LOOKUP_DEBOUNCE_MS);

  // currentData, not data: `data` still holds the previous number's certificate.
  const { currentData, isFetching, isError } = useGetIsccCertificateQuery(debounced, {
    skip: !debounced,
  });

  return useMemo(() => {
    if (!number) return CERTIFICATE_STATES.idle;
    if (isFetching || debounced !== number) return CERTIFICATE_STATES.checking;
    if (isError) return CERTIFICATE_STATES.notFound;
    return resolveCertificateState(currentData, dateOfIssuance);
  }, [number, debounced, isFetching, isError, currentData, dateOfIssuance]);
};

const buildMultisiteTransferOptions = createYesNoOptions(MULTISITE_TRANSFER_VALUE);
const buildGhgDefaultOptions = createYesNoOptions(GHG_DEFAULT_VALUE);
const buildYesNoOptions = createYesNoOptions(YES_NO_VALUE);

const buildChainOfCustodyOptions = (t) => [
  { value: CHAIN_OF_CUSTODY_OPTION.massBalance, label: t('CHAIN_OF_CUSTODY_MASS_BALANCE') },
  {
    value: CHAIN_OF_CUSTODY_OPTION.physicalSegregation,
    label: t('CHAIN_OF_CUSTODY_PHYSICAL_SEGREGATION'),
  },
];

const buildEscaBonusCapOptions = (t) => [
  { value: ESCA_BONUS_CAP.notClaimed, label: t('ESCA_BONUS_CAP_NOT_CLAIMED') },
  { value: ESCA_BONUS_CAP.manureBonus, label: t('ESCA_BONUS_CAP_MANURE_BONUS') },
  { value: ESCA_BONUS_CAP.noCapBefore2013, label: t('ESCA_BONUS_CAP_NO_CAP_BEFORE_2013') },
  { value: ESCA_BONUS_CAP.biofuelAfter2023, label: t('ESCA_BONUS_CAP_BIOFUEL_AFTER_2023') },
  { value: ESCA_BONUS_CAP.biocharUse, label: t('ESCA_BONUS_CAP_BIOCHAR_USE') },
  { value: ESCA_BONUS_CAP.priorEscaClaims, label: t('ESCA_BONUS_CAP_PRIOR_ESCA_CLAIMS') },
];

// Everything <ValidateDeclarationForm> needs from the server — mirrors useContractFormOptions.
export const useValidateDeclarationFormOptions = () => {
  const t = useTranslations();
  const { certificateType, sites, materials, facilityOptions, materialsLoading } =
    useModuleLookups();
  const { data: suppliers = EMPTY_LIST, isLoading: suppliersLoading } = useGetSuppliersQuery();

  return useMemo(
    () => ({
      sites,
      materials,
      certificateType,
      facilityOptions,
      supplierOptions: buildLookupOptions(suppliers),
      feedstockCategoryOptions: buildFeedstockCategoryOptions(t),
      allocationPreferenceOptions: buildAllocationPreferenceOptions(t, {
        includeSingleCounting: false,
      }),
      multisiteTransferOptions: buildMultisiteTransferOptions(t),
      ghgDefaultOptions: buildGhgDefaultOptions(t),
      yesNoOptions: buildYesNoOptions(t),
      chainOfCustodyOptions: buildChainOfCustodyOptions(t),
      escaBonusCapOptions: buildEscaBonusCapOptions(t),
      countryOptions: buildCountryOptions(COUNTRY_CODES),
      rawMaterialTypeOptions: buildRawMaterialTypeOptions(),
      productTypeOptions: buildProductTypeOptions(),
      isLoading: materialsLoading || suppliersLoading,
    }),
    [
      sites,
      materials,
      certificateType,
      facilityOptions,
      suppliers,
      materialsLoading,
      suppliersLoading,
      t,
    ],
  );
};

/**
 * The quantity section's derived columns. The declaration is always entered in the
 * material's declaration unit, so the working column comes from `POST /units/convert` —
 * the same call the contract and shipment forms make — using the material's own density.
 */
export const useDeclarationQuantityColumns = ({ materials, materialId, quantity }) => {
  const debouncedQuantity = useDebounce(quantity);

  const material = useMemo(
    () => (materials ?? []).find((item) => item.id === materialId) ?? null,
    [materials, materialId],
  );

  // Normalised, so the payload and the mirrored column agree on which unit is which.
  const conversionMaterial = useMemo(() => {
    if (!material) return null;
    return {
      declarationUnit: normalizeUnit(material.declarationUnit),
      workingUnit: normalizeUnit(material.workingUnit),
    };
  }, [material]);

  const conversionArgs = useMemo(() => {
    if (!conversionMaterial?.workingUnit || !debouncedQuantity) return skipToken;
    const density = material.density ? String(material.density) : null;
    const { declarationUnit, workingUnit } = conversionMaterial;
    // A mass-to-volume pair has nothing to convert with until the material carries one.
    if (needsDensity(declarationUnit, workingUnit) && !density) return skipToken;
    return {
      material: conversionMaterial,
      unit: declarationUnit,
      quantity: debouncedQuantity,
      density,
    };
  }, [conversionMaterial, material, debouncedQuantity]);

  const { data: conversion } = useConvertUnitsQuery(conversionArgs);

  return useMemo(
    () => resolveDeclarationQuantityColumns({ materials, materialId, quantity, conversion }),
    [materials, materialId, quantity, conversion],
  );
};
